/**
 * Form body for the Filing Info modal (filing mode + filing detail).
 * @module filingInfoModalBody
 */

import {
  RadioButton,
  SearchableCard,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { RefObject, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  FILING_DETAIL_COPY,
  FILING_MODE_OPTIONS,
} from "../../../enums/filingMode";
import { RootState } from "../../../redux/store/store";
import {
  formatFilingDetail,
  getFilingDetailOptions,
} from "./filingInfoModal.utils";

/**
 * Props for {@link FilingInfoModalBody}.
 *
 * Selection is owned by the parent so Save (and other consumers) can read
 * `filingMode` and `filingDetail` without reaching into this component.
 */
interface FilingInfoModalBodyProps {
  /** Currently selected filing mode (portal, fax, call, not-applicable). */
  filingMode: string;
  /** Portal name or fax number, depending on the selected filing mode. */
  filingDetail: string;
  /** Called when the user picks a filing mode. */
  onFilingModeChange: (filingMode: string) => void;
  /** Called when the user picks or creates a filing-detail value. */
  onFilingDetailChange: (filingDetail: string) => void;
}

/**
 * Filing mode radio group and SearchableCard for portal name / fax number.
 *
 * Must render as a child of Modal so {@link useModalPortalTarget} resolves the
 * modal portal node and the SearchableCard options stack above the backdrop.
 * Options come from form_options (`portal_name` / `fax_number`) loaded at
 * auth bootstrap. The details card is shown only for portal and fax; fax
 * details are formatted as phone numbers before reaching the parent.
 *
 * @param props - Controlled filing mode, filing detail, and change handlers
 * @return JSX for the filing-info form body
 */
const FilingInfoModalBody = ({
  filingMode,
  filingDetail,
  onFilingModeChange,
  onFilingDetailChange,
}: FilingInfoModalBodyProps) => {
  const portalTarget = useModalPortalTarget();
  const detailsCopy = FILING_DETAIL_COPY[filingMode];
  const showFilingDetails = Boolean(detailsCopy);
  const { data: formOptions, loading: formOptionsLoading } = useSelector(
    (state: RootState) => state.formOptions,
  );
  const filingDetailOptions = useMemo(
    () => getFilingDetailOptions(filingMode, formOptions),
    [filingMode, formOptions],
  );

  /**
   * Forwards the selected filing mode to the parent.
   *
   * @param value - Chosen filing-mode option value
   */
  const handleFilingModeChange = (value: string) => {
    onFilingModeChange(value);
  };

  /**
   * Forwards the selected or created filing-detail value to the parent,
   * formatted as a phone number when the selected mode is fax.
   *
   * @param value - Chosen SearchableCard option value
   */
  const handleFilingDetailChange = (value: string) => {
    onFilingDetailChange(formatFilingDetail(filingMode, value));
  };

  return (
    <div className="filing-mode--container flex flex-col gap-4">
      <div className="filing-mode--selector flex flex-col gap-2">
        <div className="filing-mode--selector-title text-sm font-medium text-primaryGray-1">
          Select the mode in which the J-code / CPT code is filed :
        </div>
        <RadioButton
          name="filing-mode"
          className="filing-mode--selector-radio-button"
          onChange={handleFilingModeChange}
          selectedColor="#000000"
          options={FILING_MODE_OPTIONS}
          value={filingMode}
        />
      </div>
      {showFilingDetails && (
        <div className="filing-mode--details text-sm font-medium text-primaryGray-1">
          <SearchableCard
            label={detailsCopy.label}
            onChange={handleFilingDetailChange}
            onCreateOption={handleFilingDetailChange}
            options={filingDetailOptions}
            loading={formOptionsLoading}
            placeholder={detailsCopy.placeholder}
            value={filingDetail}
            portalTarget={portalTarget as RefObject<HTMLElement>}
            keepTriggerMounted
            className="filing-mode--selector-searchable-card"
          />
        </div>
      )}
    </div>
  );
};

export default FilingInfoModalBody;
